<script>
import * as d3 from "d3";
import { Library } from "@observablehq/stdlib/";

export default {
  name: "DiffAreaChart",
  props: {
    data: {
      required: true,
      type: Array,
    },
    chartData: {
      required: true,
      type: Object,
    },
    duration: {
      required: true,
      type: String,
    },
  },
  data() {
    return {
      xScale: null,
      line: null,
      lines: null,
      bars: null,
      barWidth: null,
      yBarScale: null,
      yLineScale: null,
      gx: null,
      svg: null,
      xaxis: null,
      min_translate_x: null,
      max_translate_x: null,
      width: 700,
      height: 300,
      margin: 30,
      padding: 30,
      zoom_event: null,
      updated_data: null,
      clip: null,
      color: null,
      div: null,
      date_extent: null,
    };
  },
  watch: {
    data: function (newVal) {
      if (newVal && newVal.length > 0) {
        if (!this.svg) this.init();
        else if (this.svg && this.zoom_event) {
          this.updateChart();
          this.handleZoom(this.zoom_event);
        }
      }
    },
    duration: function (newVal) {
      if (newVal) {
        this.init();
      }
    },
  },
  mounted() {
    if (this.data && this.data.length > 0) this.init();
  },
  methods: {
    init() {
      let self = this;
      let colorScheme = [
        "#191a6f",
        "#e14615",
        "#1b428b",
        "#1e8ecc",
        "#bbc492",
        "#f7af5d",
        "#d9d9d9",
        "#183a7a",
        "#979797",
        "#0581c6",
      ];
      const id = this.chartData.id;
      const width = d3
        .select(".container")
        .node()
        .getBoundingClientRect().width;
      this.width = width;
      const height = 300;
      const legendHeight = 100;
      const margin = 30;
      const padding = 30;
      const adj = 30;
      const zoom = d3
        .zoom()
        .scaleExtent([1, 32])
        .extent([
          [margin, 0],
          [width - margin, height],
        ])
        // .translateExtent([
        //   [margin, -Infinity],
        //   [width - margin, Infinity],
        // ])
        .on("zoom", zoomed);

      var keys = this.data.map((elem) => {
        return elem.id;
      });

      let now = new Date(),
        scale = 1.25;
      this.min_translate_x = 0;
      this.color = d3.scaleOrdinal().domain(keys).range(colorScheme);
      this.xScale = d3.scaleUtc().range([margin, width - margin - padding]);
      this.yBarScale = d3
        .scaleLinear()
        .rangeRound([height - margin - padding, margin]);
      this.yLineScale = d3
        .scaleLinear()
        .rangeRound([height - margin - padding, margin]);

      this.updated_data = {
        line: [],
        bar: [],
      };
      this.data.map((elem) => {
        this.updated_data[elem.type].push(elem);
      });
      this.date_extent = d3.extent(this.data[0].values, function (d) {
        return d.key;
      });
      this.xScale.domain([
        new Date(this.date_extent[0]),
        new Date(this.date_extent[1]),
      ]);
      this.yLineScale
        .domain([
          0,
          d3.max(this.updated_data["line"], function (c) {
            return d3.max(c.values, function (d) {
              return d.value;
            });
          }),
        ])
        .nice();

      this.yBarScale.domain([
        0,
        d3.max(this.updated_data["bar"], function (c) {
          return d3.max(c.values, function (d) {
            return d.value;
          });
        }) || 10,
      ]);

      //-----------------------------AXES-----------------------------//
      var dates = this.data[0].values.map(function (v) {
        return v.key;
      });
      this.barWidth = d3
        .scaleBand()
        .domain(dates)
        .range([margin, width - margin - padding])
        .padding(0.1);

      var yBarAxis = (g, y) =>
        g
          .attr("transform", `translate(${width - 2 * margin},0)`)
          .call(d3.axisRight(y).ticks(5, "s"));
      var yLineAxis = (g, y) =>
        g
          .attr("transform", `translate(${margin + padding},0)`)
          .call(d3.axisLeft(y).ticks(5, "s"));

      this.xaxis = (g, x) =>
        g
          .attr(
            "transform",
            `translate(${margin},${height - margin - padding})`
          )
          .call(
            d3
              .axisBottom(x)
              .ticks(width / 80)
              .tickSizeOuter(0)
          );
      this.div = d3
        .select("body")
        .append("div")
        .attr("class", "tooltip primaryDark--text rounded primaryDark--text")
        .style("opacity", 0);
      this.svg = d3
        .select(`div#${this.chartData.id}`)
        .append("svg")
        .attr("viewBox", [0, 0, width, height + legendHeight])
        .classed("svg-content", true);
      const library = new Library();
      this.clip = library.DOM.uid("clip");

      this.svg
        .append("clipPath")
        .attr("id", this.clip.id)
        .append("rect")
        .attr("x", margin)
        .attr("y", margin)
        .attr("width", width - 2 * margin - 2 * padding)
        .attr("height", height - 2 * margin - padding);

      //----------------------------LINES-----------------------------//

      this.line = d3
        .area()
        .x(function (d) {
          return self.xScale(d.key);
        })
        .y0(self.yLineScale(0))
        .y1(function (d) {
          return self.yLineScale(d.value);
        });

      //-------------------------2. DRAWING---------------------------//
      //-----------------------------AXES-----------------------------//
      this.gx = this.svg
        .append("g")
        .attr("class", "axis")
        .attr(
          "transform",
          "translate(" + padding + "," + (height - 2 * padding) + ")"
        )
        .call(this.xaxis, this.xScale);
      this.gx
        .append("text")
        .attr("x", "650px")
        .attr("y", "30px")
        .attr("text-anchor", "middle")
        .text(this.chartData.xAxis);

      let yaxis = this.svg
        .append("g")
        .attr("class", "axis")
        .attr("transform", "translate(" + 2 * padding + ",0)")
        .call(yLineAxis, this.yLineScale);
      yaxis
        .append("text")
        .attr("x", "-130px")
        .attr("y", "-40px")
        .attr("text-anchor", "middle")
        .attr("transform", "rotate(-90)")
        .text(this.chartData.yAxis);

      //----------------------------LINES-----------------------------//
      this.addLinesAndBars();

      yaxis = this.svg
        .append("g")
        .attr("class", "axis")
        .attr("transform", "translate(" + width + ",20)")
        .call(yBarAxis, this.yBarScale);
      yaxis
        .append("text")
        .attr("x", "-130px")
        .attr("y", "40px")
        .attr("text-anchor", "middle")
        .attr("transform", "rotate(-90)")
        .text("Y Bar Label");

      let new_date = new Date();
      new_date.setDate(new_date.getDate() - 1);

      let from_date = new Date();
      from_date.setDate(from_date.getDate() - parseInt(this.duration));

      this.max_translate_x =
        width - this.xScale(new Date(now)) - margin - margin;

      this.min_translate_x = -margin;
      this.svg
        .call(zoom)
        .transition()
        .duration(750)
        .call(zoom.scaleTo, this.duration == 1 ? 14 : 2, [
          this.xScale(from_date),
          0,
        ]);

      function zoomed(event) {
        self.zoom_event = event;
        let current_max = self.date_extent[1].getTime();
        let min = self.date_extent[0];
        if (!isNaN(self.max_translate_x) && current_max > now.getTime()) {
          zoom.translate([self.max_translate_x, 0]);
        }
        self.handleZoom(event);
      }
    },
    handleZoom(event) {
      let self = this;
      const xz = event.transform.rescaleX(this.xScale);
      this.barWidth.range(
        [0, this.width - this.margin - this.padding].map((d) =>
          event.transform.applyX(d)
        )
      );
      let line = d3
        .line()
        .curve(d3.curveMonotoneX)
        .x(function (d) {
          return xz(d.key);
        })
        .y(function (d) {
          return self.yLineScale(d.value);
        });
      let area = d3
        .area()
        .x(function (d) {
          return xz(d.key);
        })
        .y0(self.yLineScale(0))
        .y1(function (d) {
          return self.yLineScale(d.value);
        });

      this.lines.selectAll("path").attr("d", function (d) {
        return area(d.values);
      });
      this.lines
        .selectAll("circle")
        .attr("r", (d) =>
          event.transform.k * 0.5 > 2 ? 2 : event.transform.k * 0.5
        )
        .attr("cx", (d) => xz(d.key))
        .attr("cy", (d) => this.yLineScale(d.value));
      this.bars
        .selectAll("rect")
        .attr("x", (d) => xz(d.key))
        .attr("width", this.barWidth.bandwidth() / 2);
      this.gx.call(this.xaxis, xz);
    },
    updateChart() {
      this.updated_data = {
        line: [],
        bar: [],
      };
      this.data.map((elem) => {
        this.updated_data[elem.type].push(elem);
      });
      let self = this;
      this.date_extent = d3.extent(this.data[0].values, function (d) {
        return d.key;
      });
      this.min_translate_x =
        this.zoom_event.transform.x -
        this.xScale(new Date(this.date_extent[0]));
      this.barWidth.range(
        [0, this.width - this.margin - this.padding].map((d) =>
          self.zoom_event.transform.applyX(d)
        )
      );
      this.lines.remove();
      this.bars.remove();
      this.addLinesAndBars();
      this.gx.call(this.xaxis, this.xScale);
    },
    addLinesAndBars() {
      let self = this;
      this.lines = this.svg
        .selectAll("lines")
        .data(this.updated_data["line"])
        .enter()
        .append("g")
        .attr("transform", "translate(" + this.padding + ",0)")
        .attr("class", "lines")
        .each(function (d, i) {
          let label = d.id;
          d3.select(this)
            .append("path")
            .attr("clip-path", self.clip)
            .attr("id", "line-" + d.id)
            .attr("opacity", 0.4)
            .attr("fill", self.color(d.id))
            .attr("stroke", self.color(d.id))
            .attr("d", function (d, i) {
              return self.line(d.values);
            });

          d3.select(this)
            .selectAll("circle")
            .append("g")
            .attr("class", "points pointer")
            .data(d.values)
            .enter()
            .append("circle")
            .attr("class", "point pointer")
            .attr("clip-path", self.clip)
            .attr("r", 1)
            .attr("cx", (d) => self.xScale(d.key))
            .attr("cy", (d) => self.yLineScale(d.value))
            .on("mouseover", function (event, elem) {
              self.div.transition().duration(200).style("opacity", 1);
              self.div
                .html(
                  "<div>" +
                    label +
                    "</div><div class='primaryGrey--text caption'>" +
                    elem.key +
                    "</div><div class='font-weight-bold'>" +
                    elem.value +
                    "</div>"
                )
                .style("left", event.pageX - 100 + "px")
                .style("top", event.pageY - 120 + "px");
            })
            .on("mouseout", function (d) {
              self.div.transition().duration(500).style("opacity", 0);
            });
        });
      this.bars = this.svg
        .selectAll(".bars")
        .data(this.updated_data["bar"])
        .enter()
        .append("g")
        .attr("transform", "translate(" + this.padding + ",0)")
        .attr("class", (d) => `bars`)
        .attr("id", (d) => `bars-${d.id}`)
        .each(function (d, i) {
          let label = d.id;
          d3.select(this)
            .selectAll("rect")
            .data(d.values)
            .enter()
            .append("rect")
            .attr("class", "bar")
            .attr("clip-path", self.clip)
            .attr("x", function (d) {
              return self.xScale(d.key) - self.barWidth.bandwidth() / 4;
            })
            .attr("y", function (d) {
              return self.yBarScale(d.value);
            })
            .attr("width", self.barWidth.bandwidth() / 2)
            .attr("height", function (d) {
              return (
                self.height -
                self.margin -
                self.padding -
                self.yBarScale(d.value)
              );
            })
            .attr("fill", self.color(d.id))
            .on("mouseover", function (event, elem) {
              self.div.transition().duration(200).style("opacity", 1);
              self.div
                .html(
                  "<div>" +
                    label +
                    "</div><div class='primaryGrey--text caption'>" +
                    elem.key +
                    "</div><div class='font-weight-bold'>" +
                    elem.value +
                    "</div>"
                )
                .style("left", event.pageX - 100 + "px")
                .style("top", event.pageY - 120 + "px");
            })
            .on("mouseout", function (d) {
              self.div.transition().duration(500).style("opacity", 0);
            });
        });

      var keys = this.data.map((elem) => {
        return elem.label;
      });
      // Add one dot in the legend for each name.
      const legends = this.svg
        .append("g")
        .attr("transform", "translate(0," + this.height + ")");

      legends
        .selectAll("legend")
        .data(keys)
        .enter()
        .append("g")
        .attr("transform", (d, i) => {
          return "translate(" + (this.padding + i * 180) + ",0)";
        })
        .attr("class", "legend")
        .each(function (d, i) {
          d3.select(this)
            .append("circle")
            .attr("cx", 50)
            .attr("cy", function (d, i) {
              return 50 + i * 25;
            })
            .attr("r", 7)
            .style("fill", function (d) {
              return self.color(d);
            });

          d3.select(this)
            .append("text")
            .attr("x", 60)
            .attr("y", function (d, i) {
              return 50 + i * 25;
            })
            .style("fill", function (d) {
              return self.color(d);
            })
            .text(function (d) {
              return d;
            })
            .attr("text-anchor", "left")
            .style("alignment-baseline", "middle");
        })
        .on("mouseover", function (event, d) {
          d3.selectAll(`div#${self.chartData.id} #line-${d}`).node() &&
            d3
              .selectAll(`div#${self.chartData.id} #line-${d}`)
              .node()
              .classList.add("shadow");
          d3.selectAll(`div#${self.chartData.id} #bars-${d} rect`) &&
            d3
              .selectAll(`div#${self.chartData.id} #bars-${d} rect`)
              .attr("class", "bar shadow");
        })
        .on("mouseout", function (event, d) {
          d3.selectAll(`div#${self.chartData.id} #line-${d}`).node() &&
            d3
              .selectAll(`div#${self.chartData.id} #line-${d}`)
              .node()
              .classList.remove("shadow");
          d3.selectAll(`div#${self.chartData.id} #bars-${d} rect`) &&
            d3
              .selectAll(`div#${self.chartData.id} #bars-${d} rect`)
              .attr("class", "bar");
        });
    },
  },
  computed: {},
};
</script>

<template>
  <div class="container">
    <div :id="`${chartData.id}`" class="svg-container"></div>
  </div>
</template>

<style lang="scss">
/* AXES */
/* ticks */
.axis line {
  stroke: #706f6f;
  stroke-width: 0.5;
  shape-rendering: crispEdges;
}

/* axis contour */
.axis path {
  stroke: #706f6f;
  stroke-width: 0.7;
  shape-rendering: crispEdges;
}

/* axis text */
.axis text {
  fill: #2b2929;
  font-family: "Roboto", sans-serif;
  font-size: 120%;
}

/* LINE CHART */
path.line-0 {
  fill: none;
  // stroke: #ed3700;
  stroke: url(#ptr);
}

path.line-1 {
  fill: none;
  stroke: #2b2929;
  stroke-dasharray: 2;
}

path.line-2 {
  fill: none;
  stroke: #9c9c9c;
  stroke-dasharray: 6;
}

.serie_label {
  fill: #2b2929;
  font-family: "Roboto", sans-serif;
  font-size: 80%;
}
.zoom {
  cursor: move;
  fill: none;
  pointer-events: all;
}
.bars rect {
  opacity: 0.4;
  stroke: black;
}
.pointer {
  cursor: pointer;
}
div.tooltip {
  position: absolute;
  width: 200px;
  height: 110px;
  background-color: white;
  padding: 10px;
  font-size: 12px;
  pointer-events: none;
  border: none;
  box-shadow: -3px -1px 20px 0 rgba(0, 0, 0, 0.18),
    0 6px 20px 0 rgba(153, 155, 168, 0.1);
}
div.tooltip > div {
  margin: 2px 0;
}
.caption {
  color: var(--v-primaryGrey-base);
}
.shadow,
path:hover {
  -webkit-filter: drop-shadow(4px -4px 4px rgba(0, 0, 0, 0.7));
  filter: drop-shadow(4px -4px 4px rgba(0, 0, 0, 0.7));
}
rect:hover {
  -webkit-filter: drop-shadow(2px -2px 2px rgba(0, 0, 0, 0.7));
  filter: drop-shadow(2px -2px 2px rgba(0, 0, 0, 0.7));
}
</style>